"use client";

import { useEffect, useRef, useCallback, useState } from "react";
import type {
  SSEStatusPayload,
  SSESectionPayload,
  SSECardsPayload,
  SSEDonePayload,
  SSEErrorPayload,
} from "@/types/analysis";

/** SSE 이벤트 핸들러 */
export interface SSEHandlers {
  onStatus?: (data: SSEStatusPayload) => void;
  onSection?: (data: SSESectionPayload) => void;
  onCards?: (data: SSECardsPayload) => void;
  onDone?: (data: SSEDonePayload) => void;
  onError?: (data: SSEErrorPayload) => void;
  onConnectionError?: (error: Event) => void;
}

export type SSEConnectionState = "idle" | "connecting" | "connected" | "closed" | "error";

interface UseSSEReturn {
  /** 현재 연결 상태 */
  connectionState: SSEConnectionState;
  /** SSE 연결 시작 */
  connect: (jobId: string) => void;
  /** SSE 연결 종료 */
  disconnect: () => void;
}

/**
 * SSE 스트림 관리 훅
 * 서버의 GET /api/ticker/report/stream/:id 엔드포인트에 연결
 */
export function useSSE(handlers: SSEHandlers): UseSSEReturn {
  const [connectionState, setConnectionState] = useState<SSEConnectionState>("idle");
  const eventSourceRef = useRef<EventSource | null>(null);
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setConnectionState("closed");
  }, []);

  const connect = useCallback(
    (jobId: string) => {
      // 기존 연결 정리
      disconnect();

      setConnectionState("connecting");
      const url = `/api/ticker/report/stream/${encodeURIComponent(jobId)}`;
      const es = new EventSource(url);
      eventSourceRef.current = es;

      es.onopen = () => {
        setConnectionState("connected");
      };

      es.addEventListener("status", (e) => {
        const data = JSON.parse(e.data) as SSEStatusPayload;
        handlersRef.current.onStatus?.(data);
      });

      es.addEventListener("section", (e) => {
        const data = JSON.parse(e.data) as SSESectionPayload;
        handlersRef.current.onSection?.(data);
      });

      es.addEventListener("content", (e) => {
        const data = JSON.parse(e.data) as SSECardsPayload;
        handlersRef.current.onCards?.(data);
      });

      es.addEventListener("done", (e) => {
        const data = JSON.parse(e.data) as SSEDonePayload;
        handlersRef.current.onDone?.(data);
        // 완료 시 자동 연결 종료
        es.close();
        eventSourceRef.current = null;
        setConnectionState("closed");
      });

      es.addEventListener("error", (e) => {
        // SSE error 이벤트 (서버가 보내는 에러)
        if (e instanceof MessageEvent) {
          const data = JSON.parse(e.data) as SSEErrorPayload;
          handlersRef.current.onError?.(data);
          es.close();
          eventSourceRef.current = null;
          setConnectionState("error");
        }
      });

      es.onerror = (err) => {
        // 네트워크/연결 에러 → 폴링 폴백
        if (es.readyState === EventSource.CLOSED) {
          setConnectionState("error");
          eventSourceRef.current = null;
          // 폴링 폴백: 5초마다 report API 호출해서 done인지 확인
          const pollInterval = setInterval(async () => {
            try {
              const res = await fetch(`/api/ticker/report/${encodeURIComponent(jobId)}`);
              if (!res.ok) return;
              const data = await res.json();
              if (data.status === "done") {
                clearInterval(pollInterval);
                handlersRef.current.onDone?.({ status: "done" } as SSEDonePayload);
              } else if (data.status === "error") {
                clearInterval(pollInterval);
                handlersRef.current.onError?.({ message: data.error || "분석 실패" } as SSEErrorPayload);
              }
            } catch { /* ignore */ }
          }, 5000);
          // 2분 후 폴링 중지
          setTimeout(() => clearInterval(pollInterval), 120000);
        }
      };
    },
    [disconnect]
  );

  // 컴포넌트 언마운트 시 정리
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, []);

  return { connectionState, connect, disconnect };
}
